import React, { memo, useCallback } from "react";
import {
    Grid,
    makeStyles,
    Typography,
    FormControl,
    FormHelperText,
    TextField,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { FormikValues, useField, useFormikContext } from "formik"
import {
    DIGITS_ONLY_REGEX,
    MAX_SEATS,
    MIN_SEATS,
} from "../../Static/MeetingPlanning/deliveryForm";
import { useLocalization } from "@fluent/react";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown"
import AsteriskComponent from "../Layout/Asterisk"

const useStyles = makeStyles((theme) => ({
    mr_2: {
        marginRight: theme.spacing(2)
    },

    mb_1: {
        marginBottom: theme.spacing(1)
    },

    mb_2: {
        marginBottom: theme.spacing(2)
    },

    formControl: {
        position: 'relative',
        marginBottom: theme.spacing(1)
    },

    iconButton: {
        position: 'absolute',
        right: '7px',
        top: theme.spacing(2),
        padding: 0,
        color: 'rgba(0, 0, 0, 0.54)',
        pointerEvents: 'none'
    }
}));

export interface ISeatsControlProps {
    name: string;
    label: string;
    placeholder?: string;
    required?: boolean;
}

const seatOptions = Array.from({ length: 10 }, (_, i) => (i + 1).toString());

export const NumberSelectControl = memo(({ name, label, placeholder, required }: ISeatsControlProps) => {
    const classes = useStyles();
    const { l10n } = useLocalization();
    const [field, meta] = useField<string | number>(name);
    const { setFieldValue, setFieldTouched, setFieldError } =
        useFormikContext<FormikValues>();

    const applyValue = useCallback(
        (raw: string) => {
            if (!DIGITS_ONLY_REGEX.test(raw)) return;

            const num = Number(raw);
            if (num < MIN_SEATS) {
                setFieldValue(name, "", false);
                setFieldError(
                    name,
                    l10n.getString("delivery-form-meeting-field-seats-min-length", {
                        minLength: MIN_SEATS,
                    })
                );
            } else if (num > MAX_SEATS) {
                setFieldValue(name, MAX_SEATS, false);
                setFieldError(name, undefined);
            } else {
                setFieldValue(name, num, false);
                setFieldError(name, undefined);
            }
            setTimeout(() => setFieldTouched(name, true, true), 0);
        },
        [l10n, name, setFieldError, setFieldTouched, setFieldValue]
    );

    const handleInputChange = useCallback((_, newInput, reason) => {
        if (reason === "input") applyValue(newInput);
    }, [applyValue])

    const handleOnChange = useCallback((_, newValue) => {
        if (typeof newValue === "string") applyValue(newValue);
    }, [applyValue])

    return (
        <Grid
            item
            container
            direction="column"
            wrap="nowrap"
            justifyContent="flex-start"
            className={classes.mb_2}
        >
            <Grid
                item
                container
                alignItems="center"
                justifyContent="flex-start"
                className={classes.mb_1}
            >
                <Typography variant="body1">
                    {label}
                    <AsteriskComponent required={required || false} />
                </Typography>
            </Grid>

            <Grid item container alignItems="center" className={classes.mr_2}>
                <FormControl
                    fullWidth
                    variant="outlined"
                    className={classes.formControl}
                    error={meta.touched && Boolean(meta.error)}
                >
                    <Autocomplete
                        freeSolo
                        disableClearable
                        options={seatOptions}
                        inputValue={field.value ? field.value.toString() : ""}
                        onInputChange={handleInputChange}
                        onChange={handleOnChange}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                placeholder={placeholder}
                                error={meta.touched && Boolean(meta.error)}
                            />
                        )}
                    />
                    <ArrowDropDownIcon className={classes.iconButton} />
                    {meta.touched && meta.error && (
                        <FormHelperText>{meta.error}</FormHelperText>
                    )}
                </FormControl>
            </Grid>
        </Grid>
    );
});
